import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Flame } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (mobile.length < 10 || pin.length < 4) {
      toast.error("Enter mobile number and 4-digit PIN");
      return;
    }
    setBusy(true);
    try {
      const u = await login(mobile, pin);
      toast.success(`Welcome, ${u.name}`);
      navigate(u.role === "driver" ? "/driver" : u.role === "admin" ? "/billing" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#040a1c] bg-[radial-gradient(1100px_circle_at_50%_-10%,hsl(205_100%_45%/0.55)_0%,transparent_45%),radial-gradient(700px_circle_at_50%_120%,hsl(255_70%_50%/0.45)_0%,transparent_42%)]">
      <Card className="w-full max-w-md p-8 fade-up shadow-2xl">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground mb-3 shadow-[0_0_24px_hsl(205_100%_50%/0.6)]">
            <Flame className="h-7 w-7" />
          </div>
          <h1 className="font-head text-3xl font-bold">Anila</h1>
          <p className="text-sm text-muted-foreground">Every cylinder, every rupee — under control.</p>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="mobile">Mobile number</Label>
            <Input
              id="mobile"
              data-testid="login-mobile-input"
              inputMode="numeric"
              placeholder="10-digit mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="h-12 text-base"
            />
          </div>
          <div className="space-y-2">
            <Label>4-digit PIN</Label>
            <InputOTP maxLength={4} value={pin} onChange={setPin} data-testid="login-pin-input">
              <InputOTPGroup className="gap-2">
                <InputOTPSlot index={0} className="h-12 w-12 text-lg" />
                <InputOTPSlot index={1} className="h-12 w-12 text-lg" />
                <InputOTPSlot index={2} className="h-12 w-12 text-lg" />
                <InputOTPSlot index={3} className="h-12 w-12 text-lg" />
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" className="w-full h-12 text-base" disabled={busy} data-testid="login-submit-button">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
